import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { userService, regionService, branchService, roleService } from '../services/api';
import { Card, Input, Button, Select, NormalMultiSelect, useToast } from './common';
import Breadcrumbs from './common/Breadcrumbs';
import { usePageTitle } from '../context/PageTitleContext';
import useDepartment from '../hooks/useDepartment';

const CreateEditUser = () => {
  const navigate = useNavigate();
  const dept = useDepartment();
  const { id } = useParams();
  const location = useLocation();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  
  const [userId, setUserId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState('');
  const [roles, setRoles] = useState([]);
  const [regionCodes, setRegionCodes] = useState([]);
  const [branchIds, setBranchIds] = useState([]);
  const [ticketVisibility, setTicketVisibility] = useState('assigned');
  const [regions, setRegions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userIdError, setUserIdError] = useState('');
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [roleError, setRoleError] = useState('');
  const [regionsError, setRegionsError] = useState('');
  const [branchesError, setBranchesError] = useState('');
  
  const isEditMode = !!id && location.pathname.includes('/edit/');
  const isViewMode = !!id && location.pathname.includes('/view/');

  useEffect(() => {
    if (isViewMode) {
      setPageTitle('View User');
    } else if (isEditMode) {
      setPageTitle('Edit User');
    } else {
      setPageTitle('Add User');
    }
  }, [isEditMode, isViewMode, setPageTitle]);

  useEffect(() => {
    fetchRoles();
    fetchRegions();
  }, []);

  useEffect(() => {
    if (regionCodes.length > 0) {
      fetchBranchesForRegions(regionCodes);
    } else {
      setBranches([]);
      setBranchIds([]);
    }
  }, [regionCodes]);

  useEffect(() => {
    if (isEditMode || isViewMode) {
      fetchUserData();
    }
  }, [id, isEditMode, isViewMode]);

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const response = await roleService.getAll({ limit: 1000 });
      setRoles(response.data.data);
    } catch (err) {
      console.error('Failed to load roles:', err);
      toast.error('Failed to load roles');
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchRegions = async () => {
    try {
      setLoadingRegions(true);
      const params = { noPagination: true };
      if ((isEditMode || isViewMode) && regionCodes.length > 0) {
        params.include_codes = regionCodes.join(',');
      }
      const response = await regionService.getAll(params);
      const regionOptions = response.data.data.map(r => ({
        value: r.code,
        label: r.name
      }));
      setRegions(regionOptions);
    } catch (err) {
      console.error('Failed to load regions:', err);
      toast.error('Failed to load regions');
    } finally {
      setLoadingRegions(false);
    }
  };

  const fetchBranchesForRegions = async (selectedRegionCodes) => {
    try {
      setLoadingBranches(true);
      const params = { noPagination: true };
      if ((isEditMode || isViewMode) && branchIds.length > 0) {
        params.include_ids = branchIds.join(',');
      }
      const response = await branchService.getAll(params);
      const branchOptions = response.data.data
        .filter(b => selectedRegionCodes.some(rc => {
          // Find the region by code
          const region = regions.find(r => r.value === rc);
          // Check if branch belongs to this region
          return b.region_name === region?.label;
        }))
        .map(b => ({
          value: b.id,
          label: `${b.code} - ${b.name}`
        }));
      setBranches(branchOptions);
      
      // Filter out branches that are no longer valid
      setBranchIds(prev => prev.filter(bid => branchOptions.some(b => b.value === bid)));
    } catch (err) {
      console.error('Failed to load branches:', err);
      toast.error('Failed to load branches');
    } finally {
      setLoadingBranches(false);
    }
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await userService.getById(id);
      const user = response.data;
      
      setUserId(user.user_id || '');
      setName(user.name);
      setEmail(user.email);
      setRoleId(user.role_id || '');
      setTicketVisibility(user.ticket_visibility || 'assigned');
      
      // Set regions and branches
      if (user.regions && user.regions.length > 0) {
        const userRegionCodes = user.regions.map(r => r.code);
        setRegionCodes(userRegionCodes);

        // Re-fetch regions with include_codes to show mapped regions even if inactive
        try {
          const regResponse = await regionService.getAll({ noPagination: true, include_codes: userRegionCodes.join(',') });
          setRegions(regResponse.data.data.map(r => ({ value: r.code, label: r.name })));
        } catch (err) {
          console.error('Failed to reload regions with includes:', err);
        }
        
        // Wait for branches to load based on regions
        if (user.branches && user.branches.length > 0) {
          const branchIds = user.branches.map(b => b.id);
          setBranchIds(branchIds);
        }
      }
    } catch (err) {
      toast.error('Failed to fetch user details');
      console.error(err);
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    let isValid = true;

    if (!isEditMode && !userId.trim()) {
      setUserIdError('User ID is required');
      isValid = false;
    } else {
      setUserIdError('');
    }

    if (!name.trim()) {
      setNameError('Name is required');
      isValid = false;
    } else {
      setNameError('');
    }

    if (!email.trim()) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Invalid email format');
      isValid = false;
    } else {
      setEmailError('');
    }

    if (!isEditMode && !password.trim()) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (password && password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      isValid = false;
    } else {
      setPasswordError('');
    }

    if (!roleId) {
      setRoleError('Role is required');
      isValid = false;
    } else {
      setRoleError('');
    }

    if (regionCodes.length === 0) {
      setRegionsError('At least one region must be selected');
      isValid = false;
    } else {
      setRegionsError('');
    }

    if (branchIds.length === 0) {
      setBranchesError('At least one branch must be selected');
      isValid = false;
    } else {
      setBranchesError('');
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setError('');
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const payload = {
        name: name.trim(),
        email: email.trim(),
        roleId: parseInt(roleId),
        regionCodes,
        branchIds,
        ticketVisibility
      };

      if (!isEditMode) {
        payload.userId = userId.trim();
      }

      if (password) {
        payload.password = password;
      }

      if (isEditMode) {
        await userService.update(id, payload);
        toast.success('User updated successfully');
      } else {
        await userService.create(payload);
        toast.success('User created successfully');
      }
      
      navigate('/admin/users');
    } catch (err) {
      const errorMsg = err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} user`;
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/users');
  };

  if (loading && (isEditMode || isViewMode) && !name) {
    return (
      <div className="admin-panel">
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading user details...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <Card className="w-full">
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded border-l-4 border-danger-600">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate className="w-full">
          <Breadcrumbs
            items={[
              { label: 'Users', to: '/admin/users' },
              { label: isViewMode ? 'View User' : isEditMode ? 'Edit User' : 'Add User' }
            ]}
            className="mb-6"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Input
              label="User ID"
              name="userId"
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                if (userIdError) setUserIdError('');
              }}
              placeholder="Enter unique user ID"
              required
              error={userIdError}
              disabled={loading || isViewMode || isEditMode}
            />

            <Input
              label="Name"
              name="name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError('');
              }}
              placeholder="Enter user name"
              required
              error={nameError}
              disabled={loading || isViewMode}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Input
              label="Email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError('');
              }}
              placeholder="Enter email address"
              required
              error={emailError}
              disabled={loading || isViewMode || isEditMode}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Input
              label={isEditMode ? "Password (leave blank to keep current)" : "Password"}
              name="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError('');
              }}
              placeholder="Enter password"
              required={!isEditMode}
              error={passwordError}
              disabled={loading || isViewMode}
            />

            <div>
              <Select
                label="Role"
                name="roleId"
                value={roleId}
                onChange={(e) => {
                  setRoleId(e.target.value);
                  if (roleError) setRoleError('');
                }}
                required
                error={roleError}
                disabled={loading || isViewMode || loadingRoles}
              >
                <option value="">Select Role</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.role_name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Ticket Visibility */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ticket Visibility <span className="text-danger-500">*</span>
            </label>
            <div className="flex items-center gap-6 p-3 bg-blue-50/40 rounded-lg border border-blue-100">
              {[
                { value: 'assigned', label: 'Assigned', desc: 'Only tickets assigned to the user' },
                // BioMedical has no teams, so the option means nothing there —
                // kept only if a user already carries it, so the radio still shows it
                ...(dept.isBioMedical && ticketVisibility !== 'team' ? [] : [
                  { value: 'team', label: 'Team Assigned', desc: 'Tickets assigned to user & their team' }
                ]),
                { value: 'all', label: 'Entire Tickets', desc: 'All tickets in the system' }
              ].map(opt => (
                <label
                  key={opt.value}
                  className={`inline-flex items-center gap-2 ${isViewMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  title={opt.desc}
                >
                  <input
                    type="radio"
                    name="ticket_visibility"
                    value={opt.value}
                    checked={ticketVisibility === opt.value}
                    onChange={(e) => setTicketVisibility(e.target.value)}
                    disabled={loading || isViewMode}
                    className={`h-4 w-4 accent-primary-600 ${isViewMode ? 'cursor-not-allowed opacity-60' : ''}`}
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <NormalMultiSelect
              label={dept.regions}
              name="regions"
              value={regionCodes}
              onChange={(e) => {
                setRegionCodes(e.target.value);
                if (regionsError) setRegionsError('');
              }}
              options={regions}
              placeholder={`Select ${dept.regions.toLowerCase()}`}
              required
              error={regionsError}
              disabled={loading || isViewMode}
              loading={loadingRegions}
            />

            <NormalMultiSelect
              label="Branches"
              name="branches"
              value={branchIds}
              onChange={(e) => {
                setBranchIds(e.target.value);
                if (branchesError) setBranchesError('');
              }}
              options={branches}
              placeholder={regionCodes.length > 0 ? "Select branches" : `Select ${dept.regions.toLowerCase()} first`}
              required
              error={branchesError}
              disabled={loading || isViewMode || regionCodes.length === 0}
              loading={loadingBranches}
            />
          </div>

          {isViewMode ? (
            <div className="flex w-full justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="border-primary-600 text-primary-600 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => navigate(`/admin/users/edit/${id}`)}
                className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Edit User
              </Button>
            </div>
          ) : (
            <div className="flex w-full justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
                className="border-primary-600 text-primary-600 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {loading ? 'Saving...' : isEditMode ? 'Update User' : '+ Add User'}
              </Button>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
};

export default CreateEditUser;
